/**
 * Wizard di registrazione di un'uscita (V71), cinque passi.
 *
 * L'UNITÀ È IL BIG BAG: il passo 3 non chiede un peso ma QUALI sacchi salgono
 * sul camion, e il totale è la loro somma. Un campo peso libero riaprirebbe il
 * buco che V70 ha chiuso sui prelievi — kg attribuiti a un codice o a un'area
 * che non li aveva.
 *
 * Il campo «Scansiona QR» è pensato per un lettore USB, che si comporta come
 * una tastiera e termina con Invio: non apre la fotocamera, non chiede
 * permessi, e il token risolto viene confrontato con i DISPONIBILI già
 * caricati, così il messaggio dice perché un sacco non si può prendere invece
 * di un generico «non valido».
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Truck, Check, ChevronLeft, ChevronRight, ScanLine } from 'lucide-react';
import {
  createUscita, getBigBagDisponibili, getBigBagByToken,
  type BigBagDisponibile, type TipoOperazione, type TipoUscita,
} from '../api/uscite';
import { getCerRifiutiProdotti, type CerRifiutoProdotto } from '../api/settings';
import { listTrasportatori } from '../api/trasportatori';
import type { Trasportatore } from '../types/trasportatore';
import ClienteSearch from '../components/uscite/ClienteSearch';
import StampaEtichettaModal from '../components/produzioni/StampaEtichettaModal';
import { getEtichettaBigBag } from '../api/magazzino';
import { fmtKg } from '../lib/saldo';
import { apiErrorMessage } from '../lib/apiError';

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white';
const TOTALE_PASSI = 5;

const TITOLI_PASSO = [
  'uscite.wizard.step1', 'uscite.wizard.step2', 'uscite.wizard.step3',
  'uscite.wizard.step4', 'uscite.wizard.step5',
];

function oggi(): string {
  return new Date().toISOString().slice(0, 10);
}

interface EsitoScan { tipo: 'ok' | 'ko'; testo: string; }

export default function NuovaUscitaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [passo, setPasso] = useState(1);
  const [etichetta, setEtichetta] = useState<{ id: string; token: string } | null>(null);
  const [tipo] = useState<TipoUscita>('RP');
  const [direzione, setDirezione] = useState<TipoOperazione>('vendita');
  const [rifiutoId, setRifiutoId] = useState('');
  const [rifiuti, setRifiuti] = useState<CerRifiutoProdotto[]>([]);
  const [disponibili, setDisponibili] = useState<BigBagDisponibile[]>([]);
  const [scelti, setScelti] = useState<Set<string>>(new Set());
  const [cerca, setCerca] = useState('');
  const [scanToken, setScanToken] = useState('');
  const [esitoScan, setEsitoScan] = useState<EsitoScan | null>(null);

  const [cliente, setCliente] = useState<{ id: string; ragione_sociale: string } | null>(null);
  const [numeroFir, setNumeroFir] = useState('');
  const [dataPartenza, setDataPartenza] = useState(oggi());
  const [trasportatori, setTrasportatori] = useState<Trasportatore[]>([]);
  const [trasportatoreId, setTrasportatoreId] = useState('');
  const [pesoPonte, setPesoPonte] = useState('');
  const [note, setNote] = useState('');

  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [idRifiutati, setIdRifiutati] = useState<string[]>([]);

  useEffect(() => {
    void (async () => {
      setRifiuti(await getCerRifiutiProdotti(true));
      setTrasportatori((await listTrasportatori({ limit: 100 })).items);
    })();
  }, []);

  // L'effect SOLO carica. L'azzeramento della selezione sta nell'handler che
  // lo causa (`scegliRifiuto`): resettare state dentro un effect fa un render
  // a cascata, ed e' la regola react-hooks/set-state-in-effect.
  useEffect(() => {
    void (async () => {
      setDisponibili(rifiutoId ? await getBigBagDisponibili(rifiutoId) : []);
    })();
  }, [rifiutoId]);

  // Cambiare rifiuto azzera la selezione: i sacchi scelti erano di un altro
  // materiale e tenerli sarebbe il 422 garantito al passo 5.
  function scegliRifiuto(id: string) {
    setRifiutoId(id);
    setScelti(new Set());
    setEsitoScan(null);
  }

  const perId = useMemo(() => new Map(disponibili.map((b) => [b.id, b])), [disponibili]);
  const pesoTotale = useMemo(
    () => [...scelti].reduce((somma, id) => somma + (perId.get(id)?.peso_kg ?? 0), 0),
    [scelti, perId],
  );

  const visibili = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    if (!q) return disponibili;
    return disponibili.filter((b) =>
      b.qr_token.toLowerCase().includes(q)
      || (b.area_codice ?? '').toLowerCase().includes(q)
      || (b.materiale_nome ?? '').toLowerCase().includes(q));
  }, [disponibili, cerca]);

  function toggle(id: string) {
    setScelti((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  /** Risolve il token del lettore e dice PERCHÉ un sacco non è prendibile. */
  const risolviToken = useCallback(async (token: string) => {
    const pulito = token.trim();
    if (!pulito) return;
    setScanToken('');
    const locale = disponibili.find((b) => b.qr_token === pulito);
    if (locale) {
      if (scelti.has(locale.id)) {
        setEsitoScan({ tipo: 'ko', testo: t('uscite.wizard.scan_gia_selezionato', { token: pulito }) });
      } else {
        setScelti((s) => new Set(s).add(locale.id));
        setEsitoScan({ tipo: 'ok', testo: t('uscite.wizard.scan_selezionato', { token: pulito }) });
      }
      return;
    }
    try {
      const bb = await getBigBagByToken(pulito);
      if (bb.uscita_codice) {
        setEsitoScan({ tipo: 'ko', testo: t('uscite.wizard.scan_gia_uscito', { codice: bb.uscita_codice }) });
      } else if (bb.materiale) {
        // Esiste, è libero, ma non è nei disponibili del rifiuto scelto: è di
        // un altro materiale. Il nome del materiale rende l'errore azionabile.
        setEsitoScan({ tipo: 'ko', testo: t('uscite.wizard.scan_rifiuto_diverso', { materiale: bb.materiale_nome ?? bb.materiale }) });
      } else {
        setEsitoScan({ tipo: 'ko', testo: t('uscite.wizard.scan_non_disponibile') });
      }
    } catch {
      setEsitoScan({ tipo: 'ko', testo: t('uscite.wizard.scan_non_trovato') });
    }
  }, [disponibili, scelti, t]);

  const rifiutoScelto = rifiuti.find((r) => r.id === rifiutoId) ?? null;
  const puoAvanzare =
    (passo === 1 && !!direzione)
    || (passo === 2 && !!rifiutoId)
    || (passo === 3 && scelti.size > 0)
    || (passo === 4 && !!cliente && numeroFir.trim().length > 0 && !!dataPartenza)
    || passo === 5;

  async function conferma() {
    if (!cliente) return;
    setSalvataggio(true);
    setErrore(null);
    setIdRifiutati([]);
    try {
      const creata = await createUscita({
        tipo,
        tipo_operazione: direzione,
        cer_rifiuto_id: rifiutoId,
        big_bag_ids: [...scelti],
        numero_fir: numeroFir.trim(),
        data_partenza: dataPartenza,
        customer_id: cliente.id,
        trasportatore_id: trasportatoreId || null,
        peso_ponte_kg: pesoPonte ? Number(pesoPonte) : null,
        note: note.trim() || null,
      });
      navigate(`/uscite/${creata.id}`);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = resp?.data?.detail as
        | { code?: string; big_bag_rifiutati?: { id: string }[]; big_bag_occupati?: string[] }
        | string | undefined;
      const code = typeof detail === 'object' ? detail?.code : undefined;

      // Il 422 con l'elenco e il 409 del lock dicono la stessa cosa all'utente —
      // «questi sacchi non possono partire» — e riportano entrambi al passo 3,
      // dove la selezione si corregge. Il 409 sul FIR riporta invece al 4.
      if (code === 'big_bag_non_validi' || code === 'big_bag_occupato') {
        const ids = typeof detail === 'object'
          ? (detail.big_bag_rifiutati?.map((r) => r.id) ?? detail.big_bag_occupati ?? [])
          : [];
        setIdRifiutati(ids);
        setErrore(t('uscite.wizard.errore_bigbag'));
        setPasso(3);
      } else if (code === 'fir_duplicato') {
        setErrore(t('uscite.wizard.errore_fir'));
        setPasso(4);
      } else {
        setErrore(apiErrorMessage(err));
      }
    } finally {
      setSalvataggio(false);
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Truck size={20} className="text-sage" />
        <h1 className="text-xl font-semibold text-ink-dark">{t('uscite.wizard.titolo')}</h1>
      </div>
      <p className="text-xs text-ink-soft mb-4">
        {t('uscite.wizard.passo', { n: passo, totale: TOTALE_PASSI })} · {t(TITOLI_PASSO[passo - 1] ?? '')}
      </p>

      {/* Barra di avanzamento */}
      <div className="flex gap-1 mb-6" data-testid="wizard-progress">
        {TITOLI_PASSO.map((k, i) => (
          <div key={k} className={`h-1.5 flex-1 rounded-full ${i < passo ? 'bg-sage' : 'bg-stone-mid'}`} />
        ))}
      </div>

      {errore && (
        <div className="mb-4 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger"
          data-testid="wizard-errore">
          {errore}
        </div>
      )}

      <div className="bg-white border border-stone-mid rounded-xl p-5 space-y-4">
        {/* ── 1) tipo e direzione ─────────────────────────────────────────── */}
        {passo === 1 && (
          <>
            <p className="text-sm font-medium text-ink-dark">{t('uscite.wizard.scegli_tipo')}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex items-start gap-2 border border-sage bg-sage-light/40 rounded-lg p-3 cursor-pointer">
                <input type="radio" checked readOnly data-testid="tipo-rp" className="mt-0.5" />
                <span className="text-sm text-ink-dark">{t('uscite.tipo.RP')}</span>
              </label>
              <label className="flex items-start gap-2 border border-stone-mid rounded-lg p-3 opacity-50 cursor-not-allowed"
                title={t('uscite.tipo.eow_non_disponibile')}>
                <input type="radio" disabled data-testid="tipo-eow" className="mt-0.5" />
                <span className="text-sm text-ink-soft">
                  {t('uscite.tipo.EOW')}
                  <span className="block text-xs text-ink-faint">{t('uscite.tipo.eow_non_disponibile')}</span>
                </span>
              </label>
            </div>

            <p className="text-sm font-medium text-ink-dark pt-2">{t('uscite.direzione.etichetta')}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {(['vendita', 'conferimento_a_terzi'] as TipoOperazione[]).map((d) => (
                <label key={d} data-testid={`direzione-${d}`}
                  className={`flex items-start gap-2 border rounded-lg p-3 cursor-pointer ${
                    direzione === d ? 'border-sage bg-sage-light/40' : 'border-stone-mid hover:bg-stone-bg'}`}>
                  <input type="radio" name="direzione" checked={direzione === d}
                    onChange={() => setDirezione(d)} className="mt-0.5" />
                  <span className="text-sm text-ink-dark">
                    {d === 'vendita' ? t('uscite.direzione.vendita') : t('uscite.direzione.conferimento_a_terzi')}
                    <span className="block text-xs text-ink-soft">
                      {d === 'vendita' ? t('uscite.direzione.vendita_spiega') : t('uscite.direzione.conferimento_a_terzi_spiega')}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </>
        )}

        {/* ── 2) rifiuto prodotto ─────────────────────────────────────────── */}
        {passo === 2 && (
          <>
            <label className="block text-sm font-medium text-ink-dark mb-1">{t('uscite.wizard.scegli_rifiuto')}</label>
            {rifiuti.length === 0 ? (
              <p className="text-sm text-ink-soft">{t('uscite.wizard.nessun_rifiuto')}</p>
            ) : (
              <select className={inputCls} value={rifiutoId} data-testid="select-rifiuto"
                onChange={(e) => scegliRifiuto(e.target.value)}>
                <option value="">{t('uscite.wizard.rifiuto_placeholder')}</option>
                {rifiuti.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nome_descrittivo ? `${r.nome_descrittivo} — ${r.cer}` : r.cer}
                  </option>
                ))}
              </select>
            )}
          </>
        )}

        {/* ── 3) big bag ──────────────────────────────────────────────────── */}
        {passo === 3 && (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.bigbag_titolo')}</label>
                <input type="text" className={inputCls} placeholder={t('uscite.wizard.bigbag_cerca')}
                  value={cerca} onChange={(e) => setCerca(e.target.value)} data-testid="bigbag-cerca" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <label className="flex items-center gap-1 text-xs text-ink-soft mb-1">
                  <ScanLine size={13} /> {t('uscite.wizard.scansiona')}
                </label>
                <input type="text" className={inputCls} placeholder={t('uscite.wizard.scansiona_placeholder')}
                  value={scanToken} data-testid="bigbag-scan"
                  onChange={(e) => setScanToken(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void risolviToken(scanToken); } }} />
              </div>
            </div>

            {esitoScan && (
              <div data-testid="scan-esito"
                className={`px-3 py-2 rounded text-sm ${esitoScan.tipo === 'ok'
                  ? 'bg-sage-light border border-sage/30 text-sage-dark'
                  : 'bg-accent-danger-bg border border-[#FECACA] text-accent-danger'}`}>
                {esitoScan.testo}
              </div>
            )}

            <div className="border border-stone-mid rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-bg">
                  <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">{t('uscite.wizard.bigbag_col_token')}</th>
                    <th className="px-3 py-2">{t('uscite.wizard.bigbag_col_tipologia')}</th>
                    <th className="px-3 py-2">{t('uscite.wizard.bigbag_col_area')}</th>
                    <th className="px-3 py-2 text-right">{t('uscite.wizard.bigbag_col_peso')}</th>
                    <th className="px-3 py-2">{t('uscite.wizard.bigbag_col_data')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibili.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-ink-soft">{t('uscite.wizard.bigbag_vuoto')}</td></tr>
                  ) : visibili.map((b) => (
                    <tr key={b.id} data-testid={`bigbag-riga-${b.qr_token}`}
                      className={`border-t border-stone-mid/60 ${idRifiutati.includes(b.id) ? 'bg-accent-danger-bg' : ''}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={scelti.has(b.id)} onChange={() => toggle(b.id)}
                          data-testid={`bigbag-check-${b.qr_token}`} aria-label={b.qr_token} />
                      </td>
                      {/* Il token apre l'ETICHETTA del sacco: e' il documento
                          che si ha in mano in piazzale, e si rigenera senza
                          cambiare ne' il QR ne' la data stampata. */}
                      <td className="px-3 py-2">
                        <button type="button" data-testid={`bigbag-etichetta-${b.qr_token}`}
                          title={t('uscite.wizard.bigbag_etichetta')}
                          onClick={() => setEtichetta({ id: b.id, token: b.qr_token })}
                          className="font-mono text-xs text-sage-dark hover:underline">
                          {b.qr_token.slice(0, 8)}
                        </button>
                      </td>
                      <td className="px-3 py-2">{b.tipologia_nome ?? '—'}</td>
                      <td className="px-3 py-2">{b.area_codice ?? '—'}</td>
                      <td className="px-3 py-2 text-right">{fmtKg(b.peso_kg)}</td>
                      <td className="px-3 py-2 text-ink-mid">
                        {b.data_produzione ? new Date(b.data_produzione).toLocaleDateString('it-IT') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-lg border border-stone-mid bg-sage/5 px-4 py-3" data-testid="bigbag-totale">
              <span className="text-sm font-medium text-ink-dark">
                {t('uscite.wizard.selezionati', { n: scelti.size, kg: fmtKg(pesoTotale) })}
              </span>
            </div>
          </>
        )}

        {/* ── 4) dati di accompagnamento ──────────────────────────────────── */}
        {passo === 4 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <ClienteSearch value={cliente} onChange={setCliente} label={t('uscite.wizard.cliente')}
              placeholder={t('uscite.wizard.cliente_placeholder')} testid="wizard-cliente" />
            <div>
              <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.numero_fir')}</label>
              <input type="text" className={inputCls} value={numeroFir} data-testid="wizard-fir"
                onChange={(e) => setNumeroFir(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.data_partenza')}</label>
              <input type="date" className={inputCls} value={dataPartenza}
                onChange={(e) => setDataPartenza(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.trasportatore')}</label>
              <select className={inputCls} value={trasportatoreId} onChange={(e) => setTrasportatoreId(e.target.value)}>
                <option value="">{t('uscite.wizard.trasportatore_nessuno')}</option>
                {trasportatori.map((tr) => <option key={tr.id} value={tr.id}>{tr.ragione_sociale}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.peso_ponte')}</label>
              <input type="number" step="0.01" className={inputCls} value={pesoPonte}
                onChange={(e) => setPesoPonte(e.target.value)} />
              <p className="text-[11px] text-ink-faint mt-1">{t('uscite.wizard.peso_ponte_nota')}</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.note')}</label>
              <textarea className={inputCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
        )}

        {/* ── 5) riepilogo ────────────────────────────────────────────────── */}
        {passo === 5 && (
          <dl className="text-sm divide-y divide-stone-mid" data-testid="wizard-riepilogo">
            {[
              [t('uscite.dettaglio.lbl_tipo'), t('uscite.tipo.RP')],
              [t('uscite.direzione.etichetta'), direzione === 'vendita' ? t('uscite.direzione.vendita') : t('uscite.direzione.conferimento_a_terzi')],
              [t('uscite.dettaglio.lbl_rifiuto'), rifiutoScelto ? `${rifiutoScelto.nome_descrittivo ?? ''} ${rifiutoScelto.cer}`.trim() : '—'],
              [t('uscite.wizard.riepilogo_composizione'), t('uscite.wizard.selezionati', { n: scelti.size, kg: fmtKg(pesoTotale) })],
              [t('uscite.dettaglio.lbl_cliente'), cliente?.ragione_sociale ?? '—'],
              [t('uscite.dettaglio.lbl_fir'), numeroFir || '—'],
              [t('uscite.dettaglio.lbl_data_partenza'), dataPartenza ? new Date(dataPartenza).toLocaleDateString('it-IT') : '—'],
              [t('uscite.dettaglio.lbl_trasportatore'), trasportatori.find((tr) => tr.id === trasportatoreId)?.ragione_sociale ?? '—'],
              [t('uscite.dettaglio.lbl_peso_ponte'), pesoPonte ? `${fmtKg(Number(pesoPonte))} kg` : '—'],
              [t('uscite.dettaglio.lbl_note'), note.trim() || '—'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-2">
                <dt className="text-ink-soft">{k}</dt>
                <dd className="text-ink-dark text-right">{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {/* Navigazione */}
      <div className="flex items-center justify-between mt-5">
        <button type="button" data-testid="wizard-indietro"
          onClick={() => (passo === 1 ? navigate('/uscite') : setPasso(passo - 1))}
          className="px-3 py-2 text-sm text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg flex items-center gap-1">
          <ChevronLeft size={15} /> {passo === 1 ? t('common.azioni.annulla') : t('uscite.wizard.indietro')}
        </button>
        {passo < TOTALE_PASSI ? (
          <button type="button" disabled={!puoAvanzare} data-testid="wizard-avanti"
            onClick={() => setPasso(passo + 1)}
            className="px-3 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
            {t('uscite.wizard.avanti')} <ChevronRight size={15} />
          </button>
        ) : (
          <button type="button" disabled={salvataggio || !cliente} data-testid="wizard-conferma"
            onClick={() => void conferma()}
            className="px-3 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark flex items-center gap-1 disabled:opacity-40">
            <Check size={15} /> {salvataggio ? t('uscite.wizard.salvataggio') : t('uscite.wizard.conferma')}
          </button>
        )}
      </div>
      {etichetta && (
        <StampaEtichettaModal
          title={t('uscite.wizard.bigbag_etichetta')}
          filename={`etichetta-${etichetta.token.slice(0, 8)}.pdf`}
          fetchPdf={() => getEtichettaBigBag(etichetta.id)}
          onClose={() => setEtichetta(null)}
        />
      )}
    </div>
  );
}
